import { useMemo } from 'react'
import { Chip } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

const EMPTY = '—'

export default function useHealthReportData() {
  const { t } = useTranslation()
  const list = useSelector(state => state.nurse.report.conditionList)
  const loading = useSelector(state => state.nurse.report.conditionLoading)
  const conditionTypes = useSelector(state => state.lookup.nurse.conditionType)
  const gradeLevelAndSections = useSelector(state => state.lookup.gradeLevelAndSections)

  const rows = useMemo(
    () =>
      list?.flatMap(record =>
        (record.person?.health ?? []).map(condition => ({
          id: condition.id,
          firstName: record.firstName,
          lastName: record.lastName,
          gradeLevelId: record.academicYearEnrollment?.gradeLevelId,
          gradeSection: record.academicYearEnrollment?.section?.gradeSection,
          conditionTypeId: condition.conditionTypeId,
          explanation: condition.explanation,
          medication: condition.medication,
          administer: condition.administer,
          visibleToStaff: condition.visibleToStaff,
        })),
      ) ?? [],
    [list],
  )

  const columns = useMemo(
    () => [
      {
        field: 'student',
        headerName: t('Student'),
        flex: 1.2,
        valueGetter: (value, row) => `${row.lastName ?? ''}, ${row.firstName ?? ''}`,
      },
      {
        field: 'gradeLevelId',
        headerName: t('Grade Level'),
        flex: 0.8,
        valueGetter: (value, row) =>
          gradeLevelAndSections?.find(el => el.id === row.gradeLevelId)?.name || row.gradeLevelId || '',
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'gradeSection',
        headerName: t('Section'),
        flex: 0.6,
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'conditionTypeId',
        headerName: t('Condition Type'),
        flex: 1,
        valueGetter: (value, row) =>
          conditionTypes?.find(type => type.id === row.conditionTypeId)?.name || row.conditionTypeId || '',
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'explanation',
        headerName: t('Explanation'),
        flex: 1.4,
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'medication',
        headerName: t('Medication'),
        flex: 1,
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'administer',
        headerName: t('Administer'),
        flex: 1,
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'visibleToStaff',
        headerName: t('Visible To Staff'),
        flex: 0.8,
        sortable: false,
        exportValue: row => (row.visibleToStaff ? t('Yes') : t('No')),
        renderCell: ({ row }) => (
          <Chip
            label={row.visibleToStaff ? t('YES') : t('NO')}
            color={row.visibleToStaff ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ],
    [t, conditionTypes, gradeLevelAndSections],
  )

  return { rows, columns, loading }
}
